import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Detail as DetailComponent, Confirm, useDrawer, SplitButton, useFormatter, InfoHeader } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { useLoadOne } from "src/mtska-frontend-data-provider/hooks/useLoadOne.hooks";

const dataProviderName = 'propertymanagement/property';
const clipDataProviderName = 'propertymanagement/propertyclip';
const drawerRouteBase = 'propertyManagement';
const PENDING_CLIP_STATUSES = ['queued', 'processing'];
const CLIP_POLL_INTERVAL_MS = 10000;

const hasPendingPropertyClips = (clips) => {
    if (!Array.isArray(clips)) {
        return false;
    }

    return clips.some((clip) => PENDING_CLIP_STATUSES.includes(clip?.status));
};

const resolveAgentId = (agent) => {
    if (!agent) return null;
    if (typeof agent === 'string') return agent;
    return agent._id ?? agent.id ?? null;
};

const Detail = (params) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatter();
    const { activate, close: closeDrawer } = useDrawer();
    const { userCapabilities, existingModules } = useContext(AppContext);

    const { item, save, loadOne, destroy, errorMessage, errors, saving, reloadCounter } = useDataProvider(dataProviderName, params.multipart);
    const { modelName, detailConfig } = existingModules[dataProviderName];

    useLoadOne({ loadOne, params, reloadCounter });

    const propertyId = item?.data?.id;
    const pendingClips = useMemo(
        () => hasPendingPropertyClips(item?.data?.propertyClips),
        [item?.data?.propertyClips]
    );

    useEffect(() => {
        if (!propertyId || !pendingClips) {
            return undefined;
        }

        const timer = setInterval(() => {
            loadOne(propertyId);
        }, CLIP_POLL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [propertyId, pendingClips, loadOne]);

    const handleSave = (data) => {
        const agentId = resolveAgentId(data.agent);
        const payload = { ...data, filters: params.filters };

        if (agentId) {
            payload.agent = { _id: agentId };
        }

        save(payload).then((data) => params.handleClose(params.uid, data.item.data)).catch(() => console.error('catch handleSave'));
    };

    const [confirmDelete, setConfirmDelete] = useState();
    const handleDelete = (id) => {
        setConfirmDelete(
            <Confirm
                oklabel={t("Delete")}
                okcallable={() => {
                    destroy && destroy(id);
                    closeDrawer();
                }}
                kolabel={t("Cancel")}
                kocallable={() => { setConfirmDelete(null); }}
                title={t("Item elimination")}>
                {t("Are you sure to remove this element?")}
            </Confirm>
        );
    };

    const addressParts = [
        item?.data?.address,
        item?.data?.streetNum,
        item?.data?.city,
    ].filter(Boolean);

    return (
        <>
            <DetailComponent
                values={{ ...item?.data }}
                modelName={modelName}
                detailConfig={detailConfig}
                dataProviderName={dataProviderName}
                save={handleSave}
                saving={saving}
                errors={errors}
                errorMessage={errorMessage}
                canSave={userCapabilities && userCapabilities[dataProviderName + '.edit']}
                title={(formatCurrency(item?.data?.price ?? 0)) + ' | ' + (t(item?.data?.product ?? ''))}
                subtitle={addressParts.join(', ')}
                header={
                    <InfoHeader
                        view="property"
                        item={item?.data}
                        title={(formatCurrency(item?.data?.price ?? 0)) + ' | ' + (t(item?.data?.product ?? ''))}
                        subtitle={addressParts.join(', ')}
                    />
                }
                rightButtons={[
                    <SplitButton
                        actions={[
                            {
                                disabled: userCapabilities && !userCapabilities[clipDataProviderName + '.generateclip'],
                                icon: "faFilm",
                                label: t('Generate clip'),
                                onClick: () => {
                                    activate(drawerRouteBase + '/generateClip', {
                                        property: item?.data,
                                        detailStack: params.detailStack ?? {},
                                        relatedElements: params.relatedElements ?? {},
                                    }, () => {
                                        if (item?.data?.id) {
                                            loadOne(item.data.id);
                                        }
                                    });
                                }
                            },
                            {
                                disabled: userCapabilities && !userCapabilities[dataProviderName + '.log'],
                                icon: "faClipboardList",
                                label: t('Log'),
                                onClick: () => {
                                    activate('wizards/log', {
                                        model: dataProviderName,
                                        entityId: item.data.id,
                                        detailStack: params.detailStack ?? {},
                                        title: t("Item elimination")
                                    });
                                }
                            },
                            {
                                disabled: userCapabilities && !userCapabilities[dataProviderName + '.delete'],
                                icon: "faTrash",
                                label: t('Delete'),
                                onClick: () => { handleDelete(item.data.id); }
                            },
                        ]}
                    />
                ]}
                {...params}
            />

            {confirmDelete}
        </>
    );
};

export default Detail;
